import { useCallback, useEffect, useState } from 'react';
import { GroundcoverLog, LogFilter, ProbeLogs } from '../../connection/health';

const HOUR_MS = 60 * 60 * 1000;

/** The workload spans every env, so a dev room would show production lines. */
export const envFilter = (): LogFilter | null => {
	const env = process.env.REACT_APP_NODE_ENV;
	return env && env !== 'local' ? { key: 'env', value: env } : null;
};

/**
 * Every client carries the slug in its url. Wrapped rather than anchored to the
 * end, because embed urls read `/embed/<slug>?embed-token=...`.
 */
export const meetingUrlFilter = (slug: string): LogFilter => ({
	key: 'location.url',
	value: `*${slug}*`,
});

/** Selectable in the panel; sync caps anything longer than the last entry. */
export const LOG_WINDOWS = [
	{ label: '1h', ms: HOUR_MS },
	{ label: '6h', ms: 6 * HOUR_MS },
	{ label: '24h', ms: 24 * HOUR_MS },
];

/** Six hours by default: a quiet hour is routinely empty in a dev room. */
export const DEFAULT_LOG_WINDOW_MS = 6 * HOUR_MS;

export interface UseGroundcoverLogsResult {
	logs: GroundcoverLog[];
	loaded: boolean;
	error?: string;
	limitReached: boolean;
	fetchedAt: number | null;
	refresh: () => void;
}

/** On demand, not on the room poll: an hour of logs barely moves. */
export default function useGroundcoverLogs(
	token: string | null,
	meetingSlug: string | null,
	windowMs: number = DEFAULT_LOG_WINDOW_MS,
	filters: LogFilter[] = [],
): UseGroundcoverLogsResult {
	const [logs, setLogs] = useState<GroundcoverLog[]>([]);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState<string | undefined>();
	const [limitReached, setLimitReached] = useState(false);
	const [fetchedAt, setFetchedAt] = useState<number | null>(null);
	const [nonce, setNonce] = useState(0);

	const refresh = useCallback(() => setNonce((count) => count + 1), []);

	useEffect(() => {
		if (!token) return undefined;

		let cancelled = false;
		const env = envFilter();
		const scoped = [
			...(meetingSlug ? [meetingUrlFilter(meetingSlug)] : []),
			...(env ? [env] : []),
			...filters,
		];

		ProbeLogs(token, windowMs, scoped).then((response) => {
			if (cancelled) return;

			setLoaded(true);
			setFetchedAt(Date.now());

			if (response.httpStatus !== 200) {
				setError(response.error ?? `sync answered ${response.httpStatus}`);
				setLogs([]);
				return;
			}

			setError(undefined);
			setLogs(response.body?.logs ?? []);
			setLimitReached(!!response.body?.limitReached);
		});

		return () => { cancelled = true; };
	}, [token, meetingSlug, windowMs, nonce, JSON.stringify(filters)]);

	return {
		logs, loaded, error, limitReached, fetchedAt, refresh,
	};
}
